import { createSelector } from 'reselect';
import { reduxKey } from '../constants';
import type { BoostInitializationStoreSlice } from '../types';
import type { PapiDeviation } from '@wix/da-papi-types';
import { DurationValue } from '../../_partials/DurationSlider';

const getBoostState = (state: any): BoostInitializationStoreSlice =>
  state[reduxKey] || {
    requestStatus: 'idle',
    requestError: undefined,
    isInitialized: false,
    deviation: undefined,
    tags: [],
    recommendedTags: [],
    selectedDuration: 2,
  };

export const getRecommendedTags = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): string[] => {
    return boostState.recommendedTags;
  }
);

export const getTags = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): string[] => {
    return boostState.tags;
  }
);

export const getCurrentDeviation = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): PapiDeviation | undefined => {
    return boostState.deviation;
  }
);

export const getSelectedDuration = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): DurationValue => {
    return boostState.selectedDuration;
  }
);

export const getBoostRestrictions = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice) => {
    if (!boostState.deviation) {
      return {
        canUseFreeBoost: false,
        canUsePaidBoost: false,
        freeBoostsAllowed: 0,
        freeBoostsRemaining: 0,
        boostPointsPrice: 250,
        pointsBalance: 0,
      };
    }

    const deviation: PapiDeviation = boostState.deviation;

    return {
      canUseFreeBoost:
        deviation?.extended?.boostRestrictions?.canUseFreeBoost || false,
      canUsePaidBoost:
        deviation?.extended?.boostRestrictions?.canUsePaidBoost || false,
      freeBoostsAllowed:
        deviation?.extended?.boostRestrictions?.freeBoostsAllowed || 0,
      freeBoostsRemaining:
        deviation?.extended?.boostRestrictions?.freeBoostsRemaining || 0,
      boostPointsPrice:
        deviation?.extended?.boostRestrictions?.boostPointsPrice || 250,
      pointsBalance: deviation?.extended?.boostRestrictions?.pointsBalance || 0,
    };
  }
);

export const isBoostDataLoading = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): boolean => {
    return boostState.requestStatus === 'fetching';
  }
);

export const getBoostDataError = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): string | undefined => {
    return boostState.requestError || undefined;
  }
);

export const isBoostDataInitialized = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): boolean => {
    return boostState.isInitialized;
  }
);

// Boost submission is now handled by the existing awardBadge action in coreBadge/redux
